/**
 * PhotoTypesTab - Manage the per-company photo taxonomy.
 *
 * Photo Types are scoped to a company (different business lines need
 * different vocabularies — janitorial vs. construction — so each
 * company has its own taxonomy). Subcategories nest under a Type and
 * appear in the field UX as a dependent datalist.
 *
 * Layout:
 *   - Company filter at the top — defaults to "All companies" for
 *     super_admin (their access scope is unrestricted).
 *   - Each company section lists its types. Types are shown as
 *     expandable rows; expanding reveals nested subcategories with
 *     inline add/edit/delete controls.
 *   - Seeded system defaults (is_system=true) are visually flagged
 *     and not deletable — the server enforces this; the UI hides the
 *     delete button to match.
 *
 * Edit/create UX is inline (no modal) since each entry only has a
 * name field. Renames are immediate (PATCH on blur or Enter).
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Alert } from '../../components/atoms';
import { api, camelizeKeys } from '../../services/api';
import {
    photoTypeService,
    PhotoType,
    PhotoSubcategory,
} from '../../services/photoTypeService';
import { qk } from '../../lib/queryKeys';
import { ResponsiveFilters } from '../../components/organisms/ResponsiveFilters';

interface CompanyOption { id: number; name: string }

const PhotoTypesTab: React.FC = () => {
    const queryClient = useQueryClient();

    /* Inline error surfaced from create/rename/delete actions. The list
     * query exposes its own error separately; both are merged for display. */
    const [actionError, setActionError] = useState<string | null>(null);

    /* Company filter — empty string means show all companies the user can access */
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

    /* Form state for the "create new type" inline form */
    const [newTypeName, setNewTypeName] = useState('');
    const [newTypeCompanyId, setNewTypeCompanyId] = useState<string>('');

    /* Track which types are expanded to show subcategories */
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    /* In-flight rename buffers — keyed by type/subcategory id */
    const [renameDraft, setRenameDraft] = useState<Record<string, string>>({});

    /* Buffer for "create subcategory" inline form per parent type id */
    const [newSubName, setNewSubName] = useState<Record<string, string>>({});

    /* ---------------------------------------------------------------
     * Fetch companies and photo types (TanStack Query)
     * --------------------------------------------------------------- */
    const companiesQuery = useQuery({
        queryKey: qk.companies.list({ per_page: '999' }),
        queryFn: async () => {
            const res = await api.get<any>('/companies', { per_page: '999' });
            const rows = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
            // The /companies response is NOT camelized by the api helper and the
            // Company primary key serializes as snake_case `company_id`, so reading
            // `companyId`/`id` yields NaN. Read `company_id` and drop non-finite ids.
            return rows
                .map((c: any) => ({ id: Number(c.company_id ?? c.companyId ?? c.id), name: c.name }))
                .filter((c: { id: number }) => Number.isFinite(c.id)) as CompanyOption[];
        },
    });
    /* Non-fatal: super admin can still create types by typing a company id, so
     * a companies fetch failure is ignored (empty list) just like before. */
    const companies: CompanyOption[] = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);

    /* Photo types for the current scope — re-keys on the company filter. */
    const typesQuery = useQuery({
        queryKey: qk.photoTypes.list({ company_id: selectedCompanyId }),
        queryFn: async () => {
            const params: Record<string, string> = {};
            if (selectedCompanyId) params.company_id = selectedCompanyId;
            const res = await photoTypeService.list(params);
            /* Server returns snake_case; camelize so isSystem, photoTypeId,
             * companyId, etc. resolve on the PhotoType / PhotoSubcategory
             * types declared in photoTypeService. */
            const camelized = camelizeKeys(res.data) as { data?: PhotoType[] };
            return camelized.data ?? [];
        },
    });

    const photoTypes = useMemo(() => typesQuery.data ?? [], [typesQuery.data]);
    const isLoading = typesQuery.isLoading;
    const error = actionError ?? (typesQuery.error ? (typesQuery.error as Error).message : null);

    /** Invalidate the photo-types cache after a mutation so the list refetches. */
    const invalidateTypes = () => queryClient.invalidateQueries({ queryKey: qk.photoTypes.all });

    /* ---------------------------------------------------------------
     * Group types by company for display
     * --------------------------------------------------------------- */
    const typesByCompany = useMemo(() => {
        const map = new Map<string, PhotoType[]>();
        for (const t of photoTypes) {
            const key = String(t.companyId);
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(t);
        }
        return map;
    }, [photoTypes]);

    const companyName = (companyId: string): string => {
        const found = companies.find((c) => String(c.id) === companyId);
        return found ? found.name : `Company #${companyId}`;
    };

    /* ---------------------------------------------------------------
     * Action handlers
     * --------------------------------------------------------------- */
    const toggleExpanded = (typeId: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(typeId)) next.delete(typeId);
            else next.add(typeId);
            return next;
        });
    };

    /* Mutations — each invalidates the photo-types cache on success so the
     * list refetches, replacing the old manual `await fetchTypes()` calls. */
    const createTypeMutation = useMutation({
        mutationFn: (vars: { name: string; companyId: number }) => photoTypeService.create(vars),
        onSuccess: invalidateTypes,
    });
    const updateTypeMutation = useMutation({
        mutationFn: (vars: { id: string; name: string }) => photoTypeService.update(vars.id, { name: vars.name }),
        onSuccess: invalidateTypes,
    });
    const deleteTypeMutation = useMutation({
        mutationFn: (id: string) => photoTypeService.delete(id),
        onSuccess: invalidateTypes,
    });
    const createSubMutation = useMutation({
        mutationFn: (vars: { typeId: string; name: string }) => photoTypeService.createSubcategory(vars.typeId, { name: vars.name }),
        onSuccess: invalidateTypes,
    });
    const updateSubMutation = useMutation({
        mutationFn: (vars: { id: string; name: string }) => photoTypeService.updateSubcategory(vars.id, { name: vars.name }),
        onSuccess: invalidateTypes,
    });
    const deleteSubMutation = useMutation({
        mutationFn: (id: string) => photoTypeService.deleteSubcategory(id),
        onSuccess: invalidateTypes,
    });

    const handleCreateType = async () => {
        if (!newTypeName.trim() || !newTypeCompanyId) {
            setActionError('Name and company are required.');
            return;
        }
        try {
            await createTypeMutation.mutateAsync({
                name: newTypeName.trim(),
                companyId: Number(newTypeCompanyId),
            });
            setNewTypeName('');
            setActionError(null);
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to create');
        }
    };

    /** Persist a type rename. Falls back silently if the value didn't change. */
    const handleRenameType = async (type: PhotoType) => {
        const draft = renameDraft[type.id];
        if (draft === undefined || draft.trim() === type.name) return;
        if (!draft.trim()) {
            // Reset the draft if the user cleared it
            setRenameDraft((p) => ({ ...p, [type.id]: type.name }));
            return;
        }
        try {
            await updateTypeMutation.mutateAsync({ id: type.id, name: draft.trim() });
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to rename');
        }
    };

    const handleDeleteType = async (type: PhotoType) => {
        if (type.isSystem) return; // UI hides the button but defend anyway
        if (!window.confirm(`Delete photo type "${type.name}"? Existing photos keep their reference but will lose their type tag.`)) {
            return;
        }
        try {
            await deleteTypeMutation.mutateAsync(type.id);
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to delete');
        }
    };

    const handleCreateSubcategory = async (typeId: string) => {
        const name = (newSubName[typeId] ?? '').trim();
        if (!name) return;
        try {
            await createSubMutation.mutateAsync({ typeId, name });
            setNewSubName((p) => ({ ...p, [typeId]: '' }));
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to create subcategory');
        }
    };

    const handleRenameSubcategory = async (sub: PhotoSubcategory) => {
        const draft = renameDraft[`sub:${sub.id}`];
        if (draft === undefined || draft.trim() === sub.name) return;
        if (!draft.trim()) {
            setRenameDraft((p) => ({ ...p, [`sub:${sub.id}`]: sub.name }));
            return;
        }
        try {
            await updateSubMutation.mutateAsync({ id: sub.id, name: draft.trim() });
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to rename');
        }
    };

    const handleDeleteSubcategory = async (sub: PhotoSubcategory) => {
        if (sub.isSystem) return;
        if (!window.confirm(`Delete subcategory "${sub.name}"?`)) return;
        try {
            await deleteSubMutation.mutateAsync(sub.id);
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to delete');
        }
    };

    /* ---------------------------------------------------------------
     * Render
     * --------------------------------------------------------------- */
    return (
        <div className="photo-types-tab" data-ref="organism-photo-types-tab">
            {/* Filter + new-type row. Stacks until desktop: on an iPad the
                sidebar leaves ~517px, which squeezed the name box to a sliver
                when the filter and the create form shared one line. */}
            <div className="photo-types-tab__toolbar flex flex-col desktop:flex-row items-stretch desktop:items-end gap-12px mb-16px">
                {/* The list filter folds into a Filters button + pop-up below
                    desktop, like every other list; the create form beside it
                    is an action, not a filter, so it stays on screen. */}
                <ResponsiveFilters
                    title="Filter photo types"
                    activeCount={selectedCompanyId ? 1 : 0}
                    onReset={() => setSelectedCompanyId('')}
                    dataRef="organism-photo-types-tab-filters"
                >
                    <div className="photo-types-tab__company-filter">
                        <label htmlFor="photo-types-company-filter" className="block text-12px font-medium text-brand-text mb-4px">Company</label>
                        <select
                            id="photo-types-company-filter"
                            value={selectedCompanyId}
                            onChange={(e) => setSelectedCompanyId(e.target.value)}
                            className="w-full desktop:w-auto px-12px py-8px min-h-44px desktop:min-h-0 border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text bg-white"
                        >
                            <option value="">All companies</option>
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                </ResponsiveFilters>
                <div className="hidden desktop:block flex-1" />
                <div className="photo-types-tab__create flex flex-col tablet:flex-row tablet:items-end gap-8px w-full desktop:w-auto">
                    <div className="w-full tablet:flex-1 desktop:flex-none desktop:w-auto">
                        <label className="block text-12px font-medium text-brand-text mb-4px">New type name</label>
                        <input
                            type="text"
                            value={newTypeName}
                            onChange={(e) => setNewTypeName(e.target.value)}
                            placeholder="e.g., Damage"
                            className="w-full px-12px py-8px border border-brand-border rounded-md text-16px text-brand-text"
                        />
                    </div>
                    <div>
                        <label className="block text-12px font-medium text-brand-text mb-4px">For company</label>
                        <select
                            value={newTypeCompanyId}
                            onChange={(e) => setNewTypeCompanyId(e.target.value)}
                            className="w-full tablet:w-auto px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text bg-white"
                        >
                            <option value="">Select...</option>
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <Button variant="primary" onClick={handleCreateType} className="w-full tablet:w-auto">+ Add</Button>
                </div>
            </div>

            {error && (
                <Alert variant="error" className="mb-16px">{error}</Alert>
            )}

            {isLoading && (
                <div className="px-16px py-24px text-center text-14px text-brand-text-muted">Loading photo types...</div>
            )}

            {!isLoading && photoTypes.length === 0 && !error && (
                <div className="px-16px py-32px text-center text-14px text-brand-text-muted border border-dashed border-brand-border rounded-md">
                    No photo types yet for this scope.
                </div>
            )}

            {/* Company-grouped sections */}
            {!isLoading && Array.from(typesByCompany.entries()).map(([cid, types]) => (
                <section key={cid} className="mb-24px">
                    <h2 className="text-16px font-semibold text-brand-text mb-8px">{companyName(cid)}</h2>
                    <div className="border border-brand-border rounded-md bg-white overflow-hidden">
                        {types.map((t) => {
                            const isOpen = expanded.has(t.id);
                            const draft = renameDraft[t.id] ?? t.name;
                            return (
                                <div key={t.id} className="border-b border-brand-border last:border-b-0">
                                    {/* Type header row */}
                                    <div className="flex items-center gap-8px px-12px py-8px hover:bg-brand-surface/40">
                                        <button
                                            type="button"
                                            onClick={() => toggleExpanded(t.id)}
                                            className="inline-flex items-center justify-center w-44px h-44px shrink-0 -ml-8px rounded-4px text-brand-text-muted hover:text-brand-primary hover:bg-brand-surface transition-colors"
                                            aria-label={isOpen ? 'Collapse' : 'Expand'}
                                        >
                                            {isOpen ? '▼' : '▶'}
                                        </button>
                                        <input
                                            type="text"
                                            value={draft}
                                            onChange={(e) => setRenameDraft((p) => ({ ...p, [t.id]: e.target.value }))}
                                            onBlur={() => handleRenameType(t)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                            }}
                                            className="flex-1 px-8px py-4px border border-transparent hover:border-brand-border rounded-md text-16px desktop:text-14px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                                        />
                                        {t.isSystem && (
                                            <span className="text-11px font-medium text-brand-text-muted bg-brand-surface px-6px py-2px rounded">SYSTEM</span>
                                        )}
                                        <span className="text-12px text-brand-text-muted">
                                            {t.subcategories.length} sub
                                        </span>
                                        {!t.isSystem && (
                                            <Button variant="ghost" size="sm" onClick={() => handleDeleteType(t)}>Delete</Button>
                                        )}
                                    </div>

                                    {/* Subcategories — expandable */}
                                    {isOpen && (
                                        <div className="bg-brand-surface/40 px-12px py-8px">
                                            {t.subcategories.length === 0 && (
                                                <div className="text-12px text-brand-text-muted px-4px py-2px">
                                                    No subcategories yet.
                                                </div>
                                            )}
                                            {t.subcategories.map((sub) => {
                                                const subDraft = renameDraft[`sub:${sub.id}`] ?? sub.name;
                                                return (
                                                    <div key={sub.id} className="flex items-center gap-8px py-4px">
                                                        <span className="text-brand-text-muted ml-20px">↳</span>
                                                        <input
                                                            type="text"
                                                            value={subDraft}
                                                            onChange={(e) => setRenameDraft((p) => ({ ...p, [`sub:${sub.id}`]: e.target.value }))}
                                                            onBlur={() => handleRenameSubcategory(sub)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                                            }}
                                                            className="flex-1 px-8px py-4px border border-transparent hover:border-brand-border rounded-md text-16px desktop:text-13px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary bg-white"
                                                        />
                                                        {sub.isSystem && (
                                                            <span className="text-11px font-medium text-brand-text-muted bg-brand-surface px-6px py-2px rounded">SYSTEM</span>
                                                        )}
                                                        {!sub.isSystem && (
                                                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSubcategory(sub)}>Delete</Button>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Inline add subcategory */}
                                            <div className="flex items-center gap-8px py-4px mt-4px">
                                                <span className="text-brand-text-muted ml-20px">+</span>
                                                <input
                                                    type="text"
                                                    value={newSubName[t.id] ?? ''}
                                                    onChange={(e) => setNewSubName((p) => ({ ...p, [t.id]: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleCreateSubcategory(t.id);
                                                    }}
                                                    placeholder="Add subcategory..."
                                                    className="flex-1 px-8px py-4px border border-brand-border rounded-md text-16px desktop:text-13px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white"
                                                />
                                                <Button variant="secondary" size="sm" onClick={() => handleCreateSubcategory(t.id)}>Add</Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>
            ))}
        </div>
    );
};

export default PhotoTypesTab;
